// src/Dashboard/pages/user/PrepaPhysique/components/PrepaPhysiqueExerciseTable.jsx

import React from "react";
import {
  FaExternalLinkAlt,
  FaImage,
  FaYoutube,
} from "react-icons/fa";

import {
  formatDateFR,
  getExerciseDate,
  getExerciseDone,
  getExerciseImage,
  getExerciseProgramme,
  getExerciseRepetitions,
  getExerciseVideo,
  stripHtml,
  truncate,
} from "../utils/prepaPhysiqueUserUtils";

function StatusPill({ done }) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
        done
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
          : "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20"
      }`}
    >
      {done ? "Réalisé" : "À faire"}
    </span>
  );
}

function DoneSwitch({ checked, disabled, onClick }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onClick}
      className={`relative inline-flex h-8 w-14 items-center rounded-full transition ${
        checked ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"
      } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
          checked ? "translate-x-7" : "translate-x-1"
        }`}
      />
    </button>
  );
}

export default function PrepaPhysiqueExerciseTable({
  exercises = [],
  adherentId,
  savingKey,
  onToggleDone,
  onOpenImage,
}) {
  return (
    <div className="hidden overflow-x-auto rounded-[24px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950 lg:block">
      <table className="min-w-[1100px] w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
          <tr>
            <th className="px-4 py-3">Exercice</th>
            <th className="px-4 py-3">Programme</th>
            <th className="px-4 py-3">Répétitions</th>
            <th className="px-4 py-3">Date</th>
            <th className="px-4 py-3">État</th>
            <th className="px-4 py-3">Ressources</th>
            <th className="px-4 py-3 text-center">Validation</th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
          {exercises.map((exercise) => {
            const done = getExerciseDone(exercise);
            const repetitions = getExerciseRepetitions(exercise);
            const date = getExerciseDate(exercise);
            const programme = getExerciseProgramme(exercise);
            const video = getExerciseVideo(exercise);
            const image = getExerciseImage(exercise);
            const rowKey = `${adherentId}-${exercise.id}`;
            const saving = savingKey === rowKey;

            return (
              <tr
                key={exercise.id}
                className="bg-white transition hover:bg-slate-50 dark:bg-slate-950 dark:hover:bg-slate-900"
              >
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => onOpenImage(exercise)}
                      className="h-14 w-14 shrink-0 overflow-hidden rounded-2xl bg-slate-100 ring-1 ring-slate-200 transition hover:opacity-90 dark:bg-slate-800 dark:ring-slate-700"
                      title="Agrandir l’illustration"
                    >
                      <img
                        src={image}
                        alt={exercise?.nom || "Illustration"}
                        className="h-full w-full object-cover"
                        onError={(event) => {
                          event.currentTarget.src = "/img/no-image.jpg";
                        }}
                      />
                    </button>

                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 dark:text-white">
                        {exercise?.nom || "Exercice"}
                      </p>

                      {exercise?.description && (
                        <p className="mt-1 line-clamp-2 max-w-md text-xs leading-5 text-slate-500 dark:text-slate-400">
                          {truncate(stripHtml(exercise.description), 150)}
                        </p>
                      )}
                    </div>
                  </div>
                </td>

                <td className="px-4 py-4">
                  {programme ? (
                    <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
                      {programme}
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>

                <td className="px-4 py-4 font-semibold text-slate-700 dark:text-slate-200">
                  {Number.isFinite(repetitions) ? repetitions : "—"}
                </td>

                <td className="px-4 py-4 text-slate-600 dark:text-slate-300">
                  {formatDateFR(date)}
                </td>

                <td className="px-4 py-4">
                  <StatusPill done={done} />
                </td>

                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onOpenImage(exercise)}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-700"
                      title="Voir l’image"
                    >
                      <FaImage />
                    </button>

                    {video ? (
                      <a
                        href={video}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex h-10 items-center gap-2 rounded-2xl bg-red-50 px-3 text-sm font-bold text-red-700 ring-1 ring-red-200 transition hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20"
                      >
                        <FaYoutube />
                        Vidéo
                        <FaExternalLinkAlt className="text-xs opacity-70" />
                      </a>
                    ) : (
                      <span className="text-xs text-slate-400">
                        Aucune vidéo
                      </span>
                    )}
                  </div>
                </td>

                <td className="px-4 py-4 text-center">
                  <DoneSwitch
                    checked={done}
                    disabled={saving}
                    onClick={() => onToggleDone(exercise, adherentId)}
                  />

                  {saving && (
                    <p className="mt-1 text-[11px] text-slate-400">
                      Sauvegarde…
                    </p>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}